import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, ShieldCheck, ArrowRight, Loader2, Network, Building2, FileCheck2, Cog, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  operationalPhases,
  portfolioContracts,
  systemTotals,
  fiveYearGrandTotal,
} from "@/lib/proposalData";
import Budget from "@/components/site/Budget";
import { useDeployment } from "@/lib/DeploymentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "bb_financials_unlocked";
const phaseIcons = [FileCheck2, FileCheck2, Cog, Rocket];
const fmt = (n) => `$${n.toLocaleString()}`;

export default function BudgetGate() {
  const d = useDeployment();
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY) === "1") {
      setUnlocked(true);
    }
  }, []);

  if (unlocked) return <Budget />;

  return (
    <section
      id="budget"
      data-testid="section-budget"
      className="bg-white py-24 lg:py-32 border-y border-[#CBD5E1]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Heading */}
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                The Tri-Portfolio Framework · 5-Year Multi-Agency Model
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              One city. Three agencies.<br />
              <em className="italic">1,500 households a year.</em>
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3 text-[#334155] leading-relaxed">
            <p>
              {d.budgetHubLine} <strong>Hub</strong> —
              while three competitively-selected nonprofit service providers operate as
              <strong> Spokes</strong>, each running a distinct 500-household portfolio.
              Five-year contracts with annual performance review per agency.
            </p>
          </div>
        </div>

        {/* Public preview headline stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-12">
          {[
            { k: fmt(systemTotals.grandTotal), v: "Total Annual Regional Investment (Yr 1)", accent: "#0A192F" },
            { k: systemTotals.householdsServed.toLocaleString(), v: "Households Served Annually", accent: "#0A192F" },
            { k: fmt(systemTotals.costPerHousehold), v: "Cost / Household / Year (~$230/mo)", accent: "#8B0000" },
            { k: `1 : ${systemTotals.caseload}`, v: "Service-Connector Caseload Cap", accent: "#0A192F" },
          ].map((s, i) => (
            <div key={i} data-testid={`budget-headline-${i}`} className="bg-white p-6 lg:p-7">
              <div className="font-display text-3xl lg:text-4xl" style={{ color: s.accent }}>
                {s.k}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-2 leading-snug">
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* Hub & Spokes */}
        <div className="grid lg:grid-cols-12 gap-4 mb-12">
          <div className="lg:col-span-4 bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-8 relative overflow-hidden flex flex-col justify-between min-h-[260px]">
            <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />
            <div className="relative z-10">
              <Network className="h-7 w-7 text-[#D4AF37] mb-4" />
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">
                The Hub
              </div>
              <div className="font-display text-2xl lg:text-3xl leading-tight">
                City of Baytown<br />Central Coordination Unit
              </div>
            </div>
            <div className="relative z-10 mt-6 pt-5 border-t border-[#F9F7F1]/20 font-mono text-[10px] uppercase tracking-widest text-[#CBD5E1]">
              Executive team · clinical oversight · field audit
            </div>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-3 gap-3">
            {portfolioContracts.map((p) => (
              <div key={p.id} className="bg-[#F9F7F1] border border-[#CBD5E1] p-5 lg:p-6 flex flex-col">
                <div className="flex items-start justify-between mb-3">
                  <span className="grid place-items-center w-10 h-10 border" style={{ borderColor: p.color, color: p.color }}>
                    <Building2 className="h-4 w-4" />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: p.color }}>
                    Spoke 0{p.id}
                  </span>
                </div>
                <h4 className="font-display text-base text-[#0A192F] leading-snug">{p.name}</h4>
                <p className="mt-2 text-xs text-[#475569] leading-relaxed flex-1">{p.focus}</p>
                <div className="mt-4 pt-3 border-t border-[#CBD5E1] font-mono text-[10px] uppercase tracking-widest text-[#64748B]">
                  {p.households} households
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Operational phases (publicly visible) */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-8 bg-[#8B0000]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
              Multi-Agency Operational Plan
            </span>
          </div>
          <h3 className="font-display text-2xl lg:text-3xl text-[#0A192F] leading-tight mb-6">
            From Council resolution to 1,500 households in <em className="italic">six months</em>.
          </h3>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1]">
            {operationalPhases.map((p, i) => {
              const Icon = phaseIcons[i] || FileCheck2;
              return (
                <li key={p.title} className="bg-white p-6 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <Icon className="h-5 w-5 text-[#8B0000]" />
                    <span className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000]">
                      {p.period}
                    </span>
                  </div>
                  <h4 className="font-display text-base text-[#0A192F] leading-snug">{p.title}</h4>
                  <p className="mt-3 text-xs text-[#475569] leading-relaxed flex-1">{p.body}</p>
                </li>
              );
            })}
          </ol>
        </div>

        {/* The Gate */}
        <FinancialsGate onUnlock={() => setUnlocked(true)} />

        {/* Footer tagline below gate */}
        <p className="mt-10 text-center font-mono text-[10px] uppercase tracking-widest text-[#64748B]">
          {fmt(systemTotals.grandTotal)} Year 1 · {fmt(fiveYearGrandTotal)} five-year cumulative · annual review per agency
        </p>
      </div>
    </section>
  );
}

function FinancialsGate({ onUnlock }) {
  const d = useDeployment();
  const [form, setForm] = useState({ email: "", organization: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Please enter your email and the access password.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/financials/access`, form);
      if (res.data?.granted) {
        toast.success("Access granted. Financial detail unlocked for this session.");
        if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, "1");
        onUnlock();
      } else {
        toast.error(res.data?.message || "Incorrect password.");
      }
    } catch (err) {
      toast.error("Unable to verify right now. Please email george.broughton@sterilemate.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="financials-gate"
      className="bg-[#0A192F] text-[#F9F7F1] p-7 lg:p-12 relative overflow-hidden"
    >
      <div className="absolute inset-0 grain-overlay opacity-25 pointer-events-none" />
      <div className="relative z-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-4">
            <Lock className="h-6 w-6 text-[#D4AF37]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
              Restricted · Funder &amp; Council Detail
            </span>
          </div>
          <h3 className="font-display text-3xl lg:text-4xl leading-tight">
            Full Hub line items, per-agency staffing, and the 5-year forecast are gated.
          </h3>
          <p className="mt-5 text-[#CBD5E1] leading-relaxed">
            Bridge keeps detailed compensation, per-agency staffing matrices, the
            5-year cost-of-living forecast, and the cost-avoidance comparisons
            visible only to verified Council members, funders, and partners. Enter
            your work email and the access password to unlock the full breakdown
            for this session.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-[#CBD5E1]">
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
              <span>Every access is logged with email and timestamp.</span>
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
              <span>Don't have the password? Email <a href="mailto:george.broughton@sterilemate.com" className="text-[#D4AF37] underline">george.broughton@sterilemate.com</a>.</span>
            </li>
          </ul>
        </div>

        <form
          data-testid="financials-gate-form"
          onSubmit={submit}
          className="lg:col-span-6 bg-[#F9F7F1] text-[#0A192F] p-7 lg:p-8 space-y-5"
        >
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Email <span className="text-[#8B0000] ml-1">*</span>
            </Label>
            <Input
              type="email"
              data-testid="gate-input-email"
              value={form.email}
              onChange={update("email")}
              placeholder={d.emailPlaceholder}
              className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Organization (optional)
            </Label>
            <Input
              data-testid="gate-input-org"
              value={form.organization}
              onChange={update("organization")}
              placeholder={`${d.orgPlaceholder} · Houston Endowment · etc.`}
              className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
            />
          </div>
          <div className="space-y-2">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Access Password <span className="text-[#8B0000] ml-1">*</span>
            </Label>
            <Input
              type="password"
              data-testid="gate-input-password"
              value={form.password}
              onChange={update("password")}
              placeholder="Password from George"
              className="rounded-none border-[#CBD5E1] bg-white h-11 focus-visible:ring-0 focus-visible:border-[#0A192F]"
            />
          </div>
          <Button
            type="submit"
            data-testid="gate-submit-btn"
            disabled={submitting}
            className="w-full rounded-none bg-[#0A192F] hover:bg-[#15243F] text-[#F9F7F1] h-12 font-mono text-xs uppercase tracking-widest"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Verifying
              </>
            ) : (
              <>
                Unlock Financial Detail <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#64748B]">
            Unlocks the full Hub line items, per-agency staffing, 5-year forecast, and ROI math for this browser session.
          </p>
        </form>
      </div>
    </div>
  );
}
